import React from 'react';
import { Layout } from '../components/layout/Layout';
import { ComicHeading, ComicButton, ComicPanel } from '../components/ComicPanel';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Heart, Zap, ExternalLink, Shield, Users, Target } from 'lucide-react';

const Donate = () => {
  const donationLinks = {
    '$5': 'https://www.paypal.com/ncp/payment/TAPFZSV33Q6MG',
    '$10': 'https://www.paypal.com/ncp/payment/ZWYZ53GTBJNZA',
    '$25': 'https://www.paypal.com/ncp/payment/2NW7CKN4LH68J',
    '$50': 'https://www.paypal.com/ncp/payment/X6TFTRYARNB9J',
  };

  const { user } = useAuth();

  const suggestedAmounts = ['$5', '$10', '$25', '$50'];

  const fundingRaised = 355;
  const fundingGoal = 1000;
  const fundingPercent = Math.max(0, Math.min(100, Math.round((fundingRaised / fundingGoal) * 100)));

  const cheerLines = [
    'Momentum is building — jump in early.',
    'The Piggee Lands are rising — back it early.',
    'Early backers are locking in their place — join them.',
    'Be part of the push to bring Interactive Version Ep. 1 to life.',
    'The Piggee Lands are backing this — don’t miss your spot.',
  ];

  const randomLine = cheerLines[Math.floor(Math.random() * cheerLines.length)];

  const piggeeBackers = [
    'Tara B.',
    'Sporc Master',
    'Nikki Nacker',
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-pink-500 mx-auto mb-6 flex items-center justify-center shadow-lg">
            <Heart className="w-10 h-10 text-white" />
          </div>

          <ComicHeading as="h1" className="text-4xl sm:text-5xl mb-6">
            Become a Piggee Backer
          </ComicHeading>

          <p className="text-slate-200 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Help fund the next episode of Pork Cops, support the interactive version,
            and keep the Piggee Lands growing one mission at a time.
          </p>

          <p className="text-pink-400 font-black uppercase tracking-[0.18em] text-sm sm:text-base mt-6">
            The Interactive Version for Ep.1 is currently being funded — early Piggee Backers will be recognized first.
          </p>
        </div>

        {/* Funding Progress */}
        <ComicPanel className="p-8 mb-12">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-12 h-12 bg-cyan-500 flex items-center justify-center shadow-md">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-cyan-400 uppercase font-black tracking-[0.2em] text-lg sm:text-xl text-center">
              The Interactive Version for Ep.1 Funding Progress
            </h2>
          </div>

          <p className="text-slate-300 text-center max-w-2xl mx-auto mb-4">
            Help push The Interactive Version for Ep.1. Early backers help build momentum and get recognized first.
          </p>

          <div className="max-w-3xl mx-auto">
            <div className="w-full bg-slate-900 border border-slate-700 h-5 mb-3 overflow-hidden">
              <div
                className="h-5 bg-cyan-400 transition-all duration-300"
                style={{ width: `${fundingPercent}%` }}
              />
            </div>

            <p className="text-center text-white font-black text-lg">
              ${fundingRaised.toLocaleString()} raised of ${fundingGoal.toLocaleString()} goal
            </p>

            <p className="text-center text-cyan-300 text-sm mt-3 font-semibold">
              {randomLine}
            </p>
          </div>
        </ComicPanel>

        {/* Why Support */}
        <ComicPanel className="p-8 mb-12">
          <h2 className="text-cyan-400 uppercase font-black tracking-[0.2em] text-lg sm:text-xl mb-8 text-center">
            What Your Support Helps Build
          </h2>

          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center bg-slate-900/40 border border-slate-700 p-6">
              <div className="w-14 h-14 bg-cyan-500 flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white font-black text-lg mb-2">New Episodes</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Your backing helps fund more Pork Cops story pages, stronger releases, and faster progress.
              </p>
            </div>

            <div className="flex flex-col items-center text-center bg-slate-900/40 border border-slate-700 p-6">
              <div className="w-14 h-14 bg-pink-500 flex items-center justify-center mb-4">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white font-black text-lg mb-2">Interactive Build</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Help move the interactive Pork Cops experience forward with page flow, audio, and upgrades.
              </p>
            </div>

            <div className="flex flex-col items-center text-center bg-slate-900/40 border border-slate-700 p-6">
              <div className="w-14 h-14 bg-indigo-500 flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-white font-black text-lg mb-2">Backer Rewards</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Early supporters can receive shoutouts, recognition, and future backer rewards.
              </p>
            </div>
          </div>
        </ComicPanel>

        {/* Reward Tiers */}
        <ComicPanel className="p-8 mb-12">
          <h2 className="text-pink-400 uppercase font-black tracking-[0.2em] text-lg sm:text-xl mb-8 text-center">
            Piggee Backer Recognition
          </h2>

          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-slate-900/40 border border-slate-700 p-6 text-center">
              <p className="text-cyan-300 font-black text-2xl mb-2">$5</p>
              <h3 className="text-white font-black text-lg mb-2">Site Backer</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Your name can be listed on the site as a recognized Piggee Backer.
              </p>
            </div>

            <div className="bg-slate-900/40 border-2 border-cyan-400 p-6 text-center shadow-[4px_4px_0px_0px_#00E5E5]">
              <p className="text-cyan-300 font-black text-2xl mb-2">$10</p>
              <h3 className="text-white font-black text-lg mb-2">Priority Shoutout</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Your name can be listed on the site and prioritized for Piggee Backer shoutouts.
              </p>
            </div>

            <div className="bg-slate-900/40 border border-slate-700 p-6 text-center">
              <p className="text-cyan-300 font-black text-2xl mb-2">$25+</p>
              <h3 className="text-white font-black text-lg mb-2">Feature Chance</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Higher-tier backers may be considered for future episode recognition and special extras.
              </p>
            </div>
          </div>
        </ComicPanel>

        {/* PayPal Section */}
        <ComicPanel className="p-8 mb-12 text-center">
          <h2 className="text-pink-400 uppercase font-black tracking-[0.2em] text-lg sm:text-xl mb-4">
            Back Pork Cops with PayPal
          </h2>

          <p className="text-slate-300 max-w-2xl mx-auto mb-3 leading-relaxed">
            Select an amount below to support the Interactive Version Ep.1
          </p>

          <p className="text-cyan-300 text-sm sm:text-base font-semibold max-w-2xl mx-auto mb-8">
            Early Piggee Backers will be recognized first, and the $10 level is the most popular starting point.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {suggestedAmounts.map((amount) => {
              const isPopular = amount === '$10';

              return (
                <div key={amount} className="relative">
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-400 text-black text-xs font-black px-3 py-1 uppercase border border-black z-10">
                      Most Popular
                    </div>
                  )}

                  <a
                    href={donationLinks[amount]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-24 h-24 flex items-center justify-center text-white font-black text-2xl transition-all duration-200 ${
                      isPopular
                        ? 'bg-slate-800 border-2 border-cyan-400 shadow-[4px_4px_0px_0px_#00E5E5] hover:-translate-y-1'
                        : 'bg-slate-800 border-2 border-slate-700 hover:border-cyan-400 hover:-translate-y-1'
                    }`}
                    aria-label={`Donate ${amount} with PayPal`}
                  >
                    {amount}
                  </a>
                </div>
              );
            })}
          </div>

          <div className="space-y-4">
            <a href={donationLinks['$10']} target="_blank" rel="noopener noreferrer">
              <ComicButton className="text-lg px-8 py-4">
                <ExternalLink className="w-5 h-5 mr-2 inline" />
                Back Pork Cops Now
              </ComicButton>
            </a>

            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              Secure checkout opens in PayPal for the amount you selected.
            </p>

            <p className="text-slate-500 text-xs max-w-xl mx-auto">
              Secure payment handled by PayPal. No account required.
            </p>

            <p className="text-cyan-300 text-sm max-w-2xl mx-auto mt-4 font-semibold leading-relaxed">
              Become a recognized Piggee Backer—your name could appear in future episodes and official site shoutouts. Leave your name or nickname in PayPal notes to be included, or email us at porkcops at meltedportstudios.com.
            </p>
          </div>
        </ComicPanel>

        {/* Social Proof */}
        <ComicPanel className="p-8 mb-12">
          <div className="flex items-center justify-center mb-6 gap-3">
            <div className="w-12 h-12 bg-cyan-500 flex items-center justify-center shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-cyan-400 uppercase font-black tracking-[0.2em] text-lg sm:text-xl text-center">
              Piggee Backers
            </h2>
          </div>

          <p className="text-slate-300 text-center max-w-2xl mx-auto mb-8 leading-relaxed">
            Early supporters help move Pork Cops forward. Recognized Piggee Backers can appear here and in future Pork Cops shoutouts.
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            {piggeeBackers.map((name) => (
              <div
                key={name}
                className="bg-slate-900/40 border border-slate-700 p-4 text-center"
              >
                <p className="text-white font-black">{name}</p>
              </div>
            ))}
          </div>
        </ComicPanel>

        {/* Extra Push */}
        <div className="text-center mb-14">
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Pork Cops is being built piece by piece. If you like the world, the humor, and the mission, this is your chance to help move it forward early.
          </p>
        </div>

        {/* Free Membership CTA */}
        {!user && (
          <div className="mt-12 text-center border-t border-slate-800 pt-12">
            <p className="text-slate-400 mb-4">
              Free membership is separate from backing. Create a free account to access site features and follow the project.
            </p>
            <Link to="/signup">
              <ComicButton variant="secondary">
                Join Free
              </ComicButton>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Donate;
